import { doc as docRef, setDoc } from 'firebase/firestore'
import { useState } from 'react'
import { META_PARTNER_STEPS, PASSWORD_NOTICE, PLATFORMS, PLATFORM_KEYS, accountId, resolvePlatform, type AccessStatus, type AgencySettings, type PlatformKey, type PortalConfig, type SocialSection as Doc } from '@sonder/shared'
import { useAuth } from '@/auth/AuthProvider'
import { useClientView, useMode } from '@/app/mode'
import { useSectionEditor } from '@/data/editor'
import { usePractice } from '@/data/practice'
import { reviewInfo, useNameOf } from '@/data/review'
import { useCallable, useDoc } from '@/data/hooks'
import { db } from '@/lib/firebase'
import { Button, Field, Input, Notice, PlatformCard, useToast } from '@/ui'
import { BlockHead, NoEntities, useBrands } from './common'
import { FieldEditor } from './FieldEditor'
import { SECTIONS } from '@sonder/shared'

const F = Object.fromEntries(SECTIONS.social.fields.map((f) => [f.key, f]))

export function SocialSection({ clientId, config }: { clientId: string; config: PortalConfig }) {
  const ed = useSectionEditor<Doc>(clientId, 'social')
  const brands = useBrands(clientId)
  const clientView = useClientView()
  const mode = useMode()
  const nameOf = useNameOf()
  const settings = useDoc<AgencySettings>('settings/agency')
  const { can } = useAuth()
  const { toast } = useToast()
  const canCompose = mode === 'prep' && can('clients.edit')
  // Same per-brand override the Setup page writes; merge so other brands and platforms are untouched.
  const setManaged = (brandId: string, platform: PlatformKey, managed: boolean) =>
    setDoc(docRef(db, `clients/${clientId}/config/portal`), { platformOverrides: { [brandId]: { [platform]: { managed } } } }, { merge: true }).catch((e: Error) =>
      toast(e.message ?? 'Could not update the platform', 'error'),
    )
  const doc = ed.doc
  if (!doc) return null
  if (brands.order.length === 0) return <NoEntities what="brands" hint="Accounts are per brand; add brands first." />

  const anyMeta = brands.order.some((b) => (['instagram', 'facebook'] as const).some((p) => resolvePlatform(config, b, p).include && resolvePlatform(config, b, p).managed))

  return (
    <div className="grid gap-12">
      {anyMeta && clientView && (
        <Notice title="Instagram and Facebook: partner access">{META_PARTNER_STEPS(settings.data?.metaBusinessId || '(Business ID coming from Sonder)')}</Notice>
      )}
      {brands.order.map((brandId) => (
        <section key={brandId} aria-label={brands.name(brandId)} className="grid gap-6 border-t border-ink pt-6">
          <BlockHead eyebrow="Accounts" title={brands.name(brandId)} />
          <div className="grid gap-6 xl:grid-cols-2">
            {PLATFORM_KEYS.filter((p) => resolvePlatform(config, brandId, p).include).map((platform) => {
              const id = accountId(brandId, platform)
              const item = doc.items?.[id]
              const managed = resolvePlatform(config, brandId, platform).managed
              const handle = ed.getField<string>(`items.${id}.handle`)
              const status = ed.getField<AccessStatus>(`items.${id}.status`)
              const ensure = () => {
                if (!item) ed.setRaw(`items.${id}`, { brandId, platform, secretRef: null })
              }
              return (
                <PlatformCard
                  key={id}
                  platform={platform}
                  brandName={brands.name(brandId)}
                  managed={managed}
                  onManagedChange={canCompose ? (v) => void setManaged(brandId, platform, v) : undefined}
                  mode={clientView ? 'client' : 'prep'}
                  anchors={{ handle: `items.${id}.handle`, status: `items.${id}.status` }}
                  handle={handle?.value ?? ''}
                  onHandleChange={(v) => { ensure(); ed.setValue(`items.${id}.handle`, v) }}
                  handleReview={reviewInfo(handle, nameOf, { onConfirm: (v) => ed.confirm(`items.${id}.handle`, v), onUndo: () => ed.undo(`items.${id}.handle`) })}
                  status={status?.value ?? 'not_started'}
                  onStatusChange={(v) => { ensure(); ed.setValue(`items.${id}.status`, v) }}
                  credentials={managed && PLATFORMS[platform].credentials ? <Credentials clientId={clientId} accountId={id} ed={ed} secretRef={item?.secretRef ?? null} staff={mode !== 'client'} /> : undefined}
                />
              )
            })}
          </div>
        </section>
      ))}
      {clientView && <Notice tone="warn">{PASSWORD_NOTICE}</Notice>}
    </div>
  )
}

function Credentials({ clientId, accountId: id, ed, secretRef, staff }: { clientId: string; accountId: string; ed: ReturnType<typeof useSectionEditor<Doc>>; secretRef: string | null; staff: boolean }) {
  const setSecret = useCallable<{ clientId: string; accountId: string; plaintext: string }, { secretId: string }>('setSecret')
  const reveal = useCallable<{ secretId: string }, { plaintext: string }>('revealSecret')
  const { role, can } = useAuth()
  // Passwords are encrypted and stored the moment they're saved, so practice mode leaves them alone.
  const practice = usePractice()
  const canManage = !practice && (!staff || can('credentials.manage'))
  const { toast } = useToast()
  const [pw, setPw] = useState('')
  const [busy, setBusy] = useState(false)
  const [shown, setShown] = useState<string | null>(null)

  const save = async () => {
    if (!pw) return
    setBusy(true)
    try {
      await ed.flush()
      await setSecret({ clientId, accountId: id, plaintext: pw })
      setPw('')
      toast('Password encrypted and saved', 'success')
    } catch (e) {
      toast((e as Error).message ?? 'Could not save password', 'error')
    } finally {
      setBusy(false)
    }
  }
  const onReveal = async () => {
    if (!secretRef) return
    try {
      const r = await reveal({ secretId: secretRef })
      setShown(r.plaintext)
      window.setTimeout(() => setShown(null), 30000)
    } catch (e) {
      toast((e as Error).message ?? 'Could not reveal', 'error')
    }
  }

  return (
    <>
      <FieldEditor editor={ed} path={`items.${id}.login`} def={F.login!} />
      <Field label="Password" hint={secretRef ? 'A password is on file. Enter a new one to replace it.' : undefined}>
        <div className="flex flex-wrap items-end gap-3">
          <Input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} className="flex-1" />
          <Button size="sm" variant="ghost" arrow={false} disabled={!pw || busy || !canManage} onClick={() => void save()}>
            {busy ? 'Encrypting…' : secretRef ? 'Replace' : 'Save'}
          </Button>
          {staff && role === 'staff' && can('credentials.reveal') && secretRef && (
            <Button size="sm" variant="ghost" arrow={false} onClick={() => void onReveal()}>
              Reveal
            </Button>
          )}
        </div>
      </Field>
      {shown && (
        <Notice tone="warn" title="Revealed for 30 seconds (logged)">
          <code className="font-mono">{shown}</code>
        </Notice>
      )}
    </>
  )
}
